import React, { useRef } from 'react';

export interface TabItem<K extends string = string> {
  key: K;
  label: React.ReactNode;
  /**
   * Contagem ao lado do rótulo. `segmented`/`bordered`: selo (só aparece quando > 0).
   * `pills`: "Rótulo (n)", e a aba com 0 fica desabilitada.
   */
  count?: number;
  disabled?: boolean;
  title?: string;
}

/**
 * - `segmented`: contêiner com a aba ativa "elevada" (padrão do app para trocar de visão numa tela).
 * - `pills`: pílulas soltas, para filtro dentro de uma seção.
 * - `bordered`: abas largas com borda, em colunas de mesma largura, para navegar entre seções de uma página longa.
 */
export type TabsVariant = 'segmented' | 'pills' | 'bordered';

interface Props<K extends string> {
  tabs: TabItem<K>[];
  active: K;
  onChange: (key: K) => void;
  variant?: TabsVariant;
  /** Nome acessível do grupo de abas (leitor de tela). */
  ariaLabel: string;
  className?: string;
}

const LIST: Record<TabsVariant, string> = {
  segmented: 'flex w-fit max-w-full flex-wrap gap-1 rounded-control border border-line bg-surface-muted p-1',
  pills: 'flex flex-wrap gap-1.5',
  bordered: 'grid grid-cols-[repeat(auto-fit,minmax(10rem,1fr))] gap-2',
};

const TAB_BASE =
  'inline-flex items-center gap-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 ' +
  'disabled:cursor-not-allowed disabled:opacity-50';

const TAB: Record<TabsVariant, { base: string; on: string; off: string }> = {
  segmented: {
    base: 'min-h-control-sm rounded-md px-4 py-1.5 text-sm font-medium',
    on: 'bg-surface text-fg shadow-sm',
    off: 'text-fg-muted hover:text-fg',
  },
  pills: {
    base: 'h-control-sm rounded-full px-3 text-xs font-medium',
    on: 'bg-primary text-primary-fg',
    off: 'bg-surface-muted text-fg-muted hover:bg-line disabled:hover:bg-surface-muted',
  },
  bordered: {
    base: 'h-control justify-center rounded-control border px-4 text-sm font-medium',
    on: 'border-info-line bg-info-soft text-info-fg shadow-sm',
    off: 'border-transparent bg-surface text-fg-muted hover:border-line-strong hover:bg-surface-muted hover:text-fg',
  },
};

/**
 * Abas com semântica de aba (`role="tablist"`/`tab`, `aria-selected`) e navegação por setas, Home e End.
 * Troca de aba é ação do usuário na mesma tela: para navegar entre páginas, use link.
 */
function Tabs<K extends string>({ tabs, active, onChange, variant = 'segmented', ariaLabel, className = '' }: Props<K>) {
  const refs = useRef<Record<string, HTMLButtonElement | null>>({});
  const enabled = tabs.filter((t) => !isDisabled(t, variant));

  const focusAndSelect = (key: K) => {
    refs.current[key]?.focus();
    onChange(key);
  };

  const onKeyDown = (e: React.KeyboardEvent, current: K) => {
    const idx = enabled.findIndex((t) => t.key === current);
    if (idx < 0 || enabled.length === 0) return;
    let next: number | null = null;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = (idx + 1) % enabled.length;
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = (idx - 1 + enabled.length) % enabled.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = enabled.length - 1;
    if (next === null) return;
    e.preventDefault();
    focusAndSelect(enabled[next].key);
  };

  const style = TAB[variant];
  return (
    <div role="tablist" aria-label={ariaLabel} className={`${LIST[variant]} ${className}`}>
      {tabs.map((tab) => {
        const selected = tab.key === active;
        const showBadge = variant !== 'pills' && tab.count !== undefined && tab.count > 0;
        return (
          <button
            key={tab.key}
            ref={(el) => { refs.current[tab.key] = el; }}
            type="button"
            role="tab"
            aria-selected={selected}
            tabIndex={selected ? 0 : -1}
            disabled={isDisabled(tab, variant)}
            title={tab.title}
            onClick={() => onChange(tab.key)}
            onKeyDown={(e) => onKeyDown(e, tab.key)}
            className={`${TAB_BASE} ${style.base} ${selected ? style.on : style.off}`}
          >
            <span>
              {tab.label}
              {variant === 'pills' && tab.count !== undefined ? ` (${tab.count})` : ''}
            </span>
            {showBadge && (
              <span
                className={`rounded-full px-1.5 py-0.5 text-xs font-semibold ${
                  selected ? 'bg-info-soft text-info-fg' : 'bg-line text-fg-muted'
                }`}
              >
                {tab.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

function isDisabled<K extends string>(tab: TabItem<K>, variant: TabsVariant): boolean {
  return Boolean(tab.disabled) || (variant === 'pills' && tab.count === 0);
}

export default Tabs;
